import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Save, Building2, CreditCard, FileText, Plus, Trash2, ToggleLeft, ToggleRight, Pencil, Mail, Send, Loader2, CheckCircle2, ScanLine, Eye, EyeOff, XCircle, RefreshCw, Inbox, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import MsConfigForm from "@/components/MsConfigForm";

const SA_BANKS = ["Standard Bank", "FNB (First National Bank)", "ABSA", "Nedbank", "Capitec Bank", "Discovery Bank", "African Bank", "Bidvest Bank", "Investec", "TymeBank", "Other"];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Failed to load settings")).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", settings);
      setSettings(res.data);
      toast.success("Settings saved successfully");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save settings"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!settings) return <p className="text-gray-400">Failed to load settings</p>;

  const f = (field) => (e) => setSettings({ ...settings, [field]: e.target.value });

  return (
    <div className="animate-fade-in max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Business Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure your company details for quotes and invoices</p>
        </div>
        <Button data-testid="save-settings-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <div className="space-y-6">
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-[#E63946]" />
              <h2 className="font-heading font-semibold text-lg text-[#1F2937]">Company Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Company Name</label>
                <Input data-testid="settings-company-name" value={settings.company_name || ""} onChange={f("company_name")} placeholder="Your Company (Pty) Ltd" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Trading Name</label>
                <Input value={settings.trading_name || ""} onChange={f("trading_name")} placeholder="Trading As" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Registration Number</label>
                <Input value={settings.registration_number || ""} onChange={f("registration_number")} placeholder="e.g., 2020/123456/07" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">VAT Number</label>
                <Input data-testid="settings-vat-number" value={settings.vat_number || ""} onChange={f("vat_number")} placeholder="e.g., 4123456789" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Email</label>
                <Input value={settings.email || ""} onChange={f("email")} placeholder="info@company.co.za" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Phone</label>
                <Input value={settings.phone || ""} onChange={f("phone")} placeholder="(011) 123-4567" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Website</label>
                <Input value={settings.website || ""} onChange={f("website")} placeholder="www.company.co.za" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-1">Physical Address</label>
                <Input value={settings.address || ""} onChange={f("address")} placeholder="Full business address" className="rounded-sm border-gray-300 h-10" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-[#E63946]" />
              <h2 className="font-heading font-semibold text-lg text-[#1F2937]">Banking Details</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">These details will appear on your invoices for customer payments</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Bank Name</label>
                <Select value={SA_BANKS.includes(settings.bank_name) ? settings.bank_name : "Other"} onValueChange={(v) => setSettings({ ...settings, bank_name: v === "Other" ? "" : v })}>
                  <SelectTrigger data-testid="settings-bank-name" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select bank" /></SelectTrigger>
                  <SelectContent>
                    {SA_BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!SA_BANKS.includes(settings.bank_name) && settings.bank_name !== "" && (
                  <Input value={settings.bank_name} onChange={f("bank_name")} placeholder="Bank name" className="rounded-sm border-gray-300 h-9 mt-2" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Account Name</label>
                <Input value={settings.bank_account_name || ""} onChange={f("bank_account_name")} placeholder="Account holder name" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Account Number</label>
                <Input data-testid="settings-account-number" value={settings.bank_account_number || ""} onChange={f("bank_account_number")} placeholder="Account number" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Branch Code</label>
                <Input value={settings.bank_branch_code || ""} onChange={f("bank_branch_code")} placeholder="Branch code" className="rounded-sm border-gray-300 h-10" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-1">Payment Reference Instructions</label>
                <Input value={settings.bank_reference || ""} onChange={f("bank_reference")} placeholder="e.g., Use invoice number as reference" className="rounded-sm border-gray-300 h-10" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#E63946]" />
              <h2 className="font-heading font-semibold text-lg text-[#1F2937]">Default Terms</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Default Payment Terms</label>
                <Select value={settings.default_payment_terms || "30 days"} onValueChange={(v) => setSettings({ ...settings, default_payment_terms: v })}>
                  <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash on delivery">Cash on delivery</SelectItem>
                    <SelectItem value="7 days">7 days</SelectItem>
                    <SelectItem value="14 days">14 days</SelectItem>
                    <SelectItem value="30 days">30 days</SelectItem>
                    <SelectItem value="60 days">60 days</SelectItem>
                    <SelectItem value="90 days">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Default VAT Rate (%)</label>
                <Input type="number" value={settings.default_vat_rate ?? 15} onChange={(e) => setSettings({ ...settings, default_vat_rate: parseFloat(e.target.value) || 0 })} className="rounded-sm border-gray-300 h-10" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-1">Default Quote Terms & Conditions</label>
                <textarea value={settings.default_quote_terms || ""} onChange={f("default_quote_terms")} placeholder="Terms shown on quotes..."
                  className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#374151] mb-1">Default Invoice Terms & Conditions</label>
                <textarea value={settings.default_invoice_terms || ""} onChange={f("default_invoice_terms")} placeholder="Terms shown on invoices..."
                  className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Integration */}
        {user?.role === "admin" && <VATSettingsSection />}

        {/* Email Integration */}
        {user?.role === "admin" && <EmailIntegrationSection />}

        {/* Expense Categories */}
        {user?.role === "admin" && <ExpenseCategoriesSection />}
      </div>
    </div>
  );
}

function VATSettingsSection() {
  const [vat, setVat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vat/config").then((r) => setVat(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/vat/config", vat);
      setVat(r.data);
      toast.success("VAT settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save VAT settings");
    } finally { setSaving(false); }
  };

  if (loading || !vat) return null;

  const f = (field) => (e) => setVat({ ...vat, [field]: e.target?.value ?? e });

  const VAT_CODES = [
    { code: "T1", name: "Standard Rate", rate: "15%", box: "Box 1, 5", desc: "Standard VAT rate of 15%" },
    { code: "Z", name: "Zero-Rated", rate: "0%", box: "Box 2", desc: "Exports, basic foodstuffs, fuel" },
    { code: "E", name: "Exempt", rate: "N/A", box: "Box 3", desc: "Financial services, residential rent, education" },
    { code: "EX", name: "Exports", rate: "0%", box: "Box 8", desc: "Goods/services leaving SA" },
  ];

  return (
    <Card className="border border-gray-200 rounded-sm shadow-sm" data-testid="vat-settings-section">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#E63946]" />
          <h2 className="font-heading font-semibold text-lg text-[#1F2937]">VAT (Value-Added Tax) Settings</h2>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-sm mb-5">
          <div>
            <p className="text-sm font-semibold text-[#1F2937]">Enable VAT Management</p>
            <p className="text-xs text-gray-500">Track VAT across all transactions (invoices, expenses, payments)</p>
          </div>
          <Switch data-testid="vat-enabled-toggle" checked={vat.vat_enabled} onCheckedChange={(v) => setVat({ ...vat, vat_enabled: v })} />
        </div>

        {vat.vat_enabled && (
          <div className="space-y-5">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-sm">
              <div>
                <p className="text-sm font-medium text-blue-900">Company VAT Registered</p>
                <p className="text-xs text-blue-700">Is your business registered for VAT with SARS?</p>
              </div>
              <Switch data-testid="company-vat-registered-toggle" checked={vat.company_vat_registered} onCheckedChange={(v) => setVat({ ...vat, company_vat_registered: v })} />
            </div>

            {vat.company_vat_registered && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Company VAT Number *</label>
                  <Input data-testid="company-vat-number" value={vat.company_vat_number || ""} onChange={f("company_vat_number")} placeholder="4123456789" maxLength={10} className="rounded-sm border-gray-300 h-10" />
                  <p className="text-[10px] text-gray-400 mt-1">10 digits starting with 4 (e.g., 4123456789)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">VAT Registration Date</label>
                  <Input data-testid="vat-reg-date" type="date" value={vat.vat_registration_date || ""} onChange={f("vat_registration_date")} className="rounded-sm border-gray-300 h-10" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">VAT Filing Period</label>
                  <Select value={vat.vat_periods || "bi-monthly"} onValueChange={(v) => setVat({ ...vat, vat_periods: v })}>
                    <SelectTrigger data-testid="vat-period-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bi-monthly">Bi-Monthly (Standard)</SelectItem>
                      <SelectItem value="monthly">Monthly (Turnover &gt; R30m)</SelectItem>
                      <SelectItem value="quarterly">Quarterly (Special arrangement)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Standard VAT Rate (%)</label>
                  <Input data-testid="standard-vat-rate" type="number" value={vat.standard_vat_rate ?? 15} onChange={(e) => setVat({ ...vat, standard_vat_rate: parseFloat(e.target.value) || 0 })} step="0.01" className="rounded-sm border-gray-300 h-10" />
                  <p className="text-[10px] text-gray-400 mt-1">Current SA rate: 15%</p>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-[#1F2937] mb-2">VAT Codes</h3>
              <div className="border border-gray-200 rounded-sm overflow-hidden">
                <table className="w-full text-xs" data-testid="vat-codes-table">
                  <thead className="bg-gray-50"><tr><th className="p-2 text-left font-semibold text-gray-600">Code</th><th className="p-2 text-left font-semibold text-gray-600">Description</th><th className="p-2 text-left font-semibold text-gray-600">Rate</th><th className="p-2 text-left font-semibold text-gray-600">VAT201 Box</th></tr></thead>
                  <tbody>
                    {VAT_CODES.map((c) => (
                      <tr key={c.code} className="border-t border-gray-100">
                        <td className="p-2 font-mono font-bold text-[#E63946]">{c.code}</td>
                        <td className="p-2"><span className="font-medium">{c.name}</span><br/><span className="text-gray-400">{c.desc}</span></td>
                        <td className="p-2 font-semibold">{c.rate}</td>
                        <td className="p-2 text-gray-500">{c.box}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Button data-testid="save-vat-settings" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save VAT Settings"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function EmailIntegrationSection() {
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [ocrTesting, setOcrTesting] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  useEffect(() => {
    api.get("/config/api-status").then((r) => setApiStatus(r.data)).catch(() => {}).finally(() => setStatusLoading(false));
  }, []);

  const handleTestEmail = async () => {
    if (!testEmail.trim()) return toast.error("Enter an email address");
    setSending(true);
    try {
      const r = await api.post("/email/test", { to_email: testEmail.trim() });
      if (r.data.status === "sent") { toast.success("Test email sent successfully!"); setSent(true); }
      else if (r.data.status === "skipped") toast.error("Email skipped: " + (r.data.reason || "No API key"));
      else toast.error("Email failed: " + (r.data.error || "Unknown error"));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send test email"); }
    finally { setSending(false); }
  };

  const handleTestOcr = async () => {
    setOcrTesting(true);
    setOcrResult(null);
    try {
      const r = await api.post("/ocr/test");
      setOcrResult(r.data);
      if (r.data.success) toast.success(`OCR test passed — ${r.data.response_time_ms}ms`);
      else toast.error(r.data.error || "OCR test failed");
    } catch (err) { setOcrResult({ success: false, error: err.response?.data?.detail || "Test failed" }); toast.error("OCR test failed"); }
    setOcrTesting(false);
  };

  const handleToggleOcr = async (enabled) => {
    try {
      await api.post("/ocr/toggle", { enabled });
      setApiStatus((prev) => ({ ...prev, google_vision: { ...prev?.google_vision, enabled } }));
      toast.success(`Vision OCR ${enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to toggle OCR"); }
  };

  const handleToggleMs = async (enabled) => {
    try {
      await api.post("/email-sync/toggle", { enabled });
      setApiStatus((prev) => ({ ...prev, microsoft_graph: { ...prev?.microsoft_graph, enabled } }));
      toast.success(`Email sync ${enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to toggle"); }
  };

  const resend = apiStatus?.resend || {};
  const vision = apiStatus?.google_vision || {};

  return (
    <Card data-testid="email-integration-section" className="border border-gray-200 rounded-sm shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-[#E63946]" />
          <h2 className="font-heading font-semibold text-lg text-[#1F2937]">API Configuration</h2>
        </div>

        {/* Resend Service Card */}
        <div className="border border-gray-200 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#1F2937]">Email Service (Resend)</span>
              {statusLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              ) : resend.configured ? (
                <span data-testid="resend-status-configured" className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configured</span>
              ) : (
                <span data-testid="resend-status-missing" className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Not Configured</span>
              )}
            </div>
          </div>
          <div className="bg-gray-50 rounded-sm p-3 text-sm space-y-1.5 mb-3">
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">API Key:</span>
              {statusLoading ? <span className="text-gray-300">Loading...</span> : <span data-testid="resend-masked-key" className="font-mono text-xs bg-gray-200 px-2 py-0.5 rounded">{resend.masked_key || "Not set"}</span>}
            </div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Sender:</span><span className="font-mono text-xs bg-gray-200 px-2 py-0.5 rounded">{resend.sender_email || "—"}</span></div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Status:</span>
              {resend.configured ? <span className="text-green-600 font-medium flex items-center gap-1 text-xs"><CheckCircle2 className="w-3 h-3" /> Active</span> : <span className="text-red-500 text-xs">Set RESEND_API_KEY in .env</span>}
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">Emails sent automatically with Quotes, Invoices, POs, and Quote Requests.</p>
          <div className="flex items-center gap-2">
            <Input data-testid="test-email-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Enter email to test..." className="rounded-sm border-gray-300 h-9 flex-1 max-w-sm" disabled={!resend.configured} />
            <Button data-testid="send-test-email-btn" size="sm" onClick={handleTestEmail} disabled={sending || !resend.configured} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-9">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} {sending ? "Sending..." : sent ? "Sent! Test Again" : "Test Connection"}
            </Button>
          </div>
        </div>

        {/* Google Vision AI Card */}
        <div data-testid="vision-api-section" className="border border-gray-200 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#1F2937]">Google Vision AI (OCR)</span>
              {statusLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              ) : vision.configured ? (
                <span data-testid="vision-status-configured" className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configured</span>
              ) : (
                <span data-testid="vision-status-missing" className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Not Configured</span>
              )}
            </div>
            <button onClick={() => handleToggleOcr(!vision.enabled)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${vision.enabled ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${vision.enabled ? "translate-x-4.5 ml-[18px]" : "translate-x-0.5 ml-[2px]"}`} />
            </button>
          </div>
          <div className="bg-gray-50 rounded-sm p-3 text-sm space-y-1.5 mb-3">
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Credentials:</span>
              {vision.configured ? <span className="text-green-600 text-xs font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Service account key found</span> : <span className="text-amber-600 text-xs">Set GOOGLE_APPLICATION_CREDENTIALS in .env</span>}
            </div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Enabled:</span>
              <span className={`text-xs font-medium ${vision.enabled ? "text-green-600" : "text-gray-500"}`}>{vision.enabled ? "Yes" : "No"}</span>
            </div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Usage:</span>
              <span className="text-xs">{vision.usage_this_month || 0} / 1,000 free this month</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">Scan receipts and supplier invoices to auto-extract vendor, date, amount, and invoice number. First 1,000 scans/month free.</p>
          <div className="flex items-center gap-2">
            <Button data-testid="test-ocr-btn" size="sm" variant="outline" onClick={handleTestOcr} disabled={ocrTesting || !vision.configured} className="rounded-sm h-9 border-gray-300">
              {ocrTesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 mr-1" />} {ocrTesting ? "Testing..." : "Test OCR"}
            </Button>
            {ocrResult && (
              <span className={`text-xs ${ocrResult.success ? "text-green-600" : "text-red-500"}`}>
                {ocrResult.success ? `Passed — ${ocrResult.response_time_ms}ms` : (ocrResult.error || "Failed")}
              </span>
            )}
          </div>
        </div>

        {/* Microsoft Outlook Card */}
        <div data-testid="microsoft-api-section" className="border border-gray-200 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#1F2937]">Microsoft Outlook (Email Sync)</span>
              {statusLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" /> :
                (apiStatus?.microsoft_graph?.configured ? (
                  <span data-testid="ms-status-configured" className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configured</span>
                ) : (
                  <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Not Configured</span>
                ))}
            </div>
            <button onClick={() => handleToggleMs(!(apiStatus?.microsoft_graph?.enabled))} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${apiStatus?.microsoft_graph?.enabled ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${apiStatus?.microsoft_graph?.enabled ? "translate-x-4.5 ml-[18px]" : "translate-x-0.5 ml-[2px]"}`} />
            </button>
          </div>
          <div className="bg-gray-50 rounded-sm p-3 text-sm space-y-1.5 mb-3">
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Client ID:</span>
              <span className="font-mono text-xs bg-gray-200 px-2 py-0.5 rounded">{apiStatus?.microsoft_graph?.masked_client_id || "Not set"}</span></div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Tenant ID:</span>
              <span className="font-mono text-xs bg-gray-200 px-2 py-0.5 rounded">{apiStatus?.microsoft_graph?.masked_tenant_id || "Not set"}</span></div>
            <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Mailbox:</span>
              <span className="text-xs">{apiStatus?.microsoft_graph?.sync_email || "Not configured"}</span></div>
            {apiStatus?.microsoft_graph?.last_scan_at && (
              <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Last Scan:</span>
                <span className="text-xs">{new Date(apiStatus.microsoft_graph.last_scan_at).toLocaleString()}</span></div>
            )}
            {apiStatus?.microsoft_graph?.pending_review > 0 && (
              <div className="flex items-center gap-2"><span className="text-gray-500 w-24 shrink-0">Pending:</span>
                <span className="text-xs font-semibold text-amber-600">{apiStatus.microsoft_graph.pending_review} items need review</span></div>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">Auto-import supplier invoices/quotes and log all email communications. Invoices require approval; communications log automatically.</p>
          <MsConfigForm ms={apiStatus?.microsoft_graph} onUpdated={() => api.get("/config/api-status").then((r) => setApiStatus(r.data))} />
        </div>

        <p className="text-[11px] text-gray-400">API keys are stored as environment variables and never exposed in full. Manage keys in your server's .env file.</p>
      </CardContent>
    </Card>
  );
}





function ExpenseCategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const fetchCats = () => api.get("/expense-categories").then((r) => setCategories(r.data.categories));
  useEffect(() => { fetchCats(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/expense-categories", { name: newName.trim() });
      setNewName("");
      toast.success("Category added");
      fetchCats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleToggle = async (cat) => {
    try {
      await api.put(`/expense-categories/${cat.category_id}`, { active: !cat.active });
      toast.success(`${cat.name} ${cat.active ? "deactivated" : "activated"}`);
      fetchCats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRename = async (cat) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/expense-categories/${cat.category_id}`, { name: editName.trim() });
      setEditId(null);
      toast.success("Category renamed");
      fetchCats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (cat) => {
    try {
      await api.delete(`/expense-categories/${cat.category_id}`);
      toast.success("Category deleted");
      fetchCats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="border border-gray-200 rounded-sm shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#E63946]" />
          <h2 className="text-lg font-heading font-semibold text-[#1F2937]">Expense Categories</h2>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Input data-testid="new-category-input" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name..." className="rounded-sm flex-1 h-9"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
          <Button data-testid="add-category-btn" size="sm" onClick={handleAdd} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-1">
          {categories.map((cat) => (
            <div key={cat.category_id} className={`flex items-center justify-between px-3 py-2 rounded-sm border ${cat.active ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"}`}>
              {editId === cat.category_id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-sm h-7 text-sm flex-1"
                    onKeyDown={(e) => e.key === "Enter" && handleRename(cat)} autoFocus />
                  <Button size="sm" variant="ghost" className="h-7 text-green-600" onClick={() => handleRename(cat)}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditId(null)}>Cancel</Button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-[#1F2937]">{cat.name}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600" onClick={() => { setEditId(cat.category_id); setEditName(cat.name); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggle(cat)}>
                      {cat.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500" onClick={() => handleDelete(cat)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </>
              )}
            </div>
          ))}
          {categories.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No categories yet</p>}
        </div>
      </CardContent>
    </Card>
  );
}
